import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  subscribeToAnalyticsConsent,
  type AnalyticsConsent,
} from "@/lib/analytics/consent";
import { useConsentT } from "@/components/analytics/consentLanguage";

const OPEN_SETTINGS_EVENT = "aiia:cookie-settings";

// The footers' "Cookie settings" link calls this. There is no floating button:
// it sat on top of the app's sidebar and the interview room's controls.
export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

const buttonBase =
  "h-8 w-full rounded-lg px-2.5 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600";
const primaryButton = `${buttonBase} bg-slate-900 font-medium text-white hover:bg-slate-700`;
const quietButton = `${buttonBase} text-slate-700 hover:bg-slate-100`;

export function CookieConsent() {
  const t = useConsentT();
  const [consent, setConsent] = useState<AnalyticsConsent>(() =>
    getAnalyticsConsent()
  );
  // null = closed; "notice" = the small first card; "preferences" = the toggles.
  const [view, setView] = useState<"notice" | "preferences" | null>(null);
  // Off until the visitor turns it on: no pre-ticked consent (Planet49).
  const [analyticsOn, setAnalyticsOn] = useState(false);

  useEffect(() => subscribeToAnalyticsConsent(setConsent), []);

  useEffect(() => {
    const open = () => {
      setAnalyticsOn(getAnalyticsConsent() === "accepted");
      setView("preferences");
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  const choose = (choice: "accepted" | "rejected") => {
    setAnalyticsConsent(choice);
    setView(null);
  };

  const shown = view ?? (consent === "unknown" ? "notice" : null);
  if (!shown) return null;

  return (
    <section
      aria-label={t("Cookie preferences")}
      data-consent-ui=""
      className="fixed inset-x-4 bottom-4 z-[10000] flex flex-col gap-4 rounded-[2rem] bg-white p-6 text-slate-900 shadow-[0_0_0_1px_rgba(15,23,42,0.08),0_8px_24px_rgba(15,23,42,0.08)] duration-300 animate-in fade-in-0 slide-in-from-bottom-4 sm:start-auto sm:end-4 sm:w-72"
    >
      {shown === "notice" ? (
        <>
          <p className="text-sm leading-[21px]">
            {t("We'd like to use analytics cookies, including masked session replay, to improve our site.")}{" "}
            <Link className="underline" to="/privacy-policy">
              {t("Privacy policy")}
            </Link>
          </p>
          <div className="flex flex-col gap-2.5">
            <button
              className={primaryButton}
              onClick={() => choose("accepted")}
              type="button"
            >
              {t("OK")}
            </button>
            {/* Refusing is one click and looks the same as accepting (EDPB cookie banner taskforce). */}
            <button
              className={primaryButton}
              onClick={() => choose("rejected")}
              type="button"
            >
              {t("Reject optional")}
            </button>
            <button
              className={quietButton}
              onClick={() => {
                setAnalyticsOn(false);
                setView("preferences");
              }}
              type="button"
            >
              {t("Manage preferences")}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm font-semibold">{t("Cookie preferences")}</p>
          <div className="flex flex-col gap-3 text-sm leading-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{t("Essential")}</p>
                <p className="text-slate-500">{t("Sign-in and security.")}</p>
              </div>
              <span className="shrink-0 text-xs text-slate-500">{t("Always on")}</span>
            </div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{t("Analytics")}</p>
                <p className="text-slate-500">
                  {t("Page visits, clicks and masked session replay.")}
                </p>
              </div>
              <button
                aria-checked={analyticsOn}
                aria-label={t("Analytics")}
                className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 ${analyticsOn ? "bg-slate-900" : "bg-slate-300"}`}
                onClick={() => setAnalyticsOn((on) => !on)}
                role="switch"
                type="button"
              >
                <span
                  className={`absolute start-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${analyticsOn ? "translate-x-4 rtl:-translate-x-4" : ""}`}
                />
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2.5">
            <button
              className={primaryButton}
              onClick={() => choose(analyticsOn ? "accepted" : "rejected")}
              type="button"
            >
              {t("Save preferences")}
            </button>
            <button
              className={quietButton}
              onClick={() => choose("rejected")}
              type="button"
            >
              {t("Reject optional")}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
